import type { GetServerSidePropsContext } from 'next';
import { useConfig } from 'shared/config/ConfigContext';
import { fetchVariants } from 'shared/experiment/variants';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SWRConfig } from 'swr';
import { getCurrentCancellationPolicy } from '@/api/apiInternal';
import { fetchObject } from '@/api/objectApi';
import { userLookup } from '@/api/userApi';
import type { ObjectData } from '@/domain';
import type { User } from '@/domain/userLookup';
import { ExperimentProvider } from '@/hooks/experiments';
import { DATA_KEY as CURRENT_CANCELLATION_POLICY_KEY } from '@/hooks/useCurrentCancellationPolicy';
import { USE_OBJECT_KEY } from '@/hooks/useObject';
import { CreateRentalRequestPage } from '@/scenes/createRentalRequestPage/CreateRentalRequestPage';
import { clientConfig, config } from '@/server/config';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import { wrapper } from '@/store/store';

type Props = {
    user: User | null;
    swrFallback: { [USE_OBJECT_KEY]: ObjectData };
    experiments: Record<string, string>;
};

const CreateRentalRequest = (props: Props) => {
    const { baseUrl } = useConfig();
    wrapper.useHydration(props);
    const objectData = props.swrFallback[USE_OBJECT_KEY];

    return (
        <ExperimentProvider experiments={props.experiments}>
            <SWRConfig value={{ fallback: props.swrFallback }}>
                <Head
                    title="Feriehjem og hytter - Send forespørsel | FINN reise"
                    description="Send forespørsel til utleier."
                    canonical={`${baseUrl}/reise/feriehus-hytteutleie/opprettforesporsel/?finnkode=${objectData.adId}`}
                />
                <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie">
                    <CreateRentalRequestPage user={props.user} />
                </Main>
            </SWRConfig>
        </ExperimentProvider>
    );
};

export const getServerSideProps = wrapper.getServerSideProps(() => async ({ req, res, query }: GetServerSidePropsContext) => {
    const commonProps = await buildCommonProps(req, res, query);
    const itemId = typeof query?.finnkode === 'string' ? query.finnkode : '';

    const experiments = await fetchVariants(req, res, 'travel-fhh-web', config.get('experimentsApiUrl'), [
        'travel-fhh-tjvr-request-page-drop-off-reduction',
    ]);

    const objectData = await fetchObject(itemId, commonProps.authCookies, req.cookies.USERID)
        .then((data) => data)
        .catch(() => null);

    let user: User | null;
    if (commonProps.loginId) {
        user = await userLookup(commonProps.loginId?.toString(), commonProps.loginJwt);
    } else {
        user = null;
    }

    if (!objectData) {
        return { notFound: true };
    }

    const currentCancellationPolicy = await getCurrentCancellationPolicy(objectData.adId);

    const swrFallback = {
        [USE_OBJECT_KEY]: objectData,
        [CURRENT_CANCELLATION_POLICY_KEY]: currentCancellationPolicy,
    };

    return {
        props: {
            config: clientConfig,
            ...sanitizeCommonProps(commonProps),
            user,
            swrFallback,
            experiments: experiments ?? {},
        },
    };
});

export default CreateRentalRequest;
